import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validation
    if (!formData.company || !formData.name || !formData.email) {
      alert('必須項目を入力してください');
      return;
    }

    if (formData.message.length > 500) {
      alert('ご相談内容は500文字以内で入力してください');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('company', formData.company);
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6qbn6ua739gopmlh83g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          company: '',
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-[#E8EDF5] py-20">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-black text-[#1A3A5C] mb-4">
            まずはお気軽にご相談ください
          </h2>
          <p className="text-[#4A5568] text-lg">
            初回相談は無料です。お客さまの現場の課題をお聞かせください
          </p>
        </div>

        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-12">
          <form id="contact-form" data-readdy-form onSubmit={handleSubmit}>
            {/* Company Name */}
            <div className="mb-6">
              <label htmlFor="company" className="block text-[#1A3A5C] text-sm font-bold mb-2">
                会社名 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="company"
                name="company"
                required
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="w-full bg-[#F5F7FA] border-0 rounded-lg px-4 py-4 text-[#1A3A5C] focus:outline-none focus:ring-2 focus:ring-[#1A3A5C]"
                placeholder="株式会社〇〇"
              />
            </div>

            {/* Name */}
            <div className="mb-6">
              <label htmlFor="name" className="block text-[#1A3A5C] text-sm font-bold mb-2">
                お名前 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full bg-[#F5F7FA] border-0 rounded-lg px-4 py-4 text-[#1A3A5C] focus:outline-none focus:ring-2 focus:ring-[#1A3A5C]"
                placeholder="山田 太郎"
              />
            </div>

            {/* Email */}
            <div className="mb-6">
              <label htmlFor="email" className="block text-[#1A3A5C] text-sm font-bold mb-2">
                メールアドレス <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-[#F5F7FA] border-0 rounded-lg px-4 py-4 text-[#1A3A5C] focus:outline-none focus:ring-2 focus:ring-[#1A3A5C]"
                placeholder="example@company.co.jp"
              />
            </div>

            {/* Phone */}
            <div className="mb-6">
              <label htmlFor="phone" className="block text-[#1A3A5C] text-sm font-bold mb-2">
                電話番号 <span className="text-[#4A5568] text-xs font-normal">（任意）</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full bg-[#F5F7FA] border-0 rounded-lg px-4 py-4 text-[#1A3A5C] focus:outline-none focus:ring-2 focus:ring-[#1A3A5C]"
                placeholder="03-1234-5678"
              />
            </div>

            {/* Message */}
            <div className="mb-8">
              <label htmlFor="message" className="block text-[#1A3A5C] text-sm font-bold mb-2">
                ご相談内容
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                maxLength={500}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full bg-[#F5F7FA] border-0 rounded-lg px-4 py-4 text-[#1A3A5C] focus:outline-none focus:ring-2 focus:ring-[#1A3A5C] resize-none"
                placeholder="現在の課題や、ご相談されたい内容をお聞かせください（500文字以内）"
              />
              <p className="text-[#4A5568] text-xs mt-2 text-right">
                {formData.message.length} / 500文字
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#1A3A5C] text-white py-4 rounded-xl font-bold text-lg hover:bg-[#2A4A6C] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? '送信中...' : '無料相談を申し込む'}
            </button>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 text-sm text-center">
                  お問い合わせありがとうございます。ご返信まで暫くお待ち下さい。
                </p>
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm text-center">
                  送信に失敗しました。もう一度お試しください。
                </p>
              </div>
            )}
          </form>

        </div>
      </div>
    </section>
  );
}